/**
 * AP Serial & Movement Number Lookup Tool
 * Provides production era estimates based on Audemars Piguet serial/case numbers and movement numbers
 */

(function() {
  'use strict';

  // Movement number ranges (with overlaps as per historical data)
  const MOVEMENT_RANGES = [
    { min: 2000, max: 4500, era: '1880-1889', startYear: 1880, endYear: 1889 },
    { min: 4000, max: 6500, era: '1890-1899', startYear: 1890, endYear: 1899 },
    { min: 6000, max: 14000, era: '1900-1909', startYear: 1900, endYear: 1909 },
    { min: 11000, max: 27000, era: '1910-1919', startYear: 1910, endYear: 1919 },
    { min: 23000, max: 42000, era: '1920-1929', startYear: 1920, endYear: 1929 },
    { min: 41000, max: 45000, era: '1930-1939', startYear: 1930, endYear: 1939 },
    { min: 44000, max: 60000, era: '1940-1949', startYear: 1940, endYear: 1949 },
    { min: 55000, max: 80000, era: '1950-1959', startYear: 1950, endYear: 1959 },
    { min: 72000, max: 120000, era: '1960-1969', startYear: 1960, endYear: 1969 },
    { min: 110000, max: 230000, era: '1970-1979', startYear: 1970, endYear: 1979 },
    { min: 220000, max: 350000, era: '1980-1989', startYear: 1980, endYear: 1989 },
    { min: 330000, max: 490000, era: '1990-1999', startYear: 1990, endYear: 1999 },
    { min: 475000, max: 750000, era: '2000-2009', startYear: 2000, endYear: 2009 },
    { min: 700000, max: 999999, era: '2010-2017', startYear: 2010, endYear: 2017 }
  ];

  // Serial/case number ranges by prefix
  const SERIAL_RANGES = {
    'A': { min: 101, max: 105393, startYear: 1951, endYear: 1976 },
    'B': { min: 1, max: 99999, startYear: 1975, endYear: 1990 },
    'C': { min: 1, max: 99999, startYear: 1984, endYear: 1995 },
    'D': { min: 1, max: 99999, startYear: 1991, endYear: 2000 },
    'E': { min: 1, max: 99999, startYear: 1998, endYear: 2010 },
    'F': { min: 1, max: 99999, startYear: 2003, endYear: 2010 },
    'G': { min: 1, max: 99999, startYear: 2009, endYear: 2015 },
    'H': { min: 1, max: 99999, startYear: 2011, endYear: 2015 },
    'I': { min: 1, max: 99999, startYear: 2013, endYear: 2020 },
    'J': { min: 1, max: 94000, startYear: 2015, endYear: 2020 }
  };

  /**
   * Initialize the lookup tool for a section
   */
  function initSection(sectionId) {
    const section = document.querySelector(`[data-section-id="${sectionId}"]`);
    if (!section) return;

    // Tab switching
    const tabs = section.querySelectorAll('.ap-serial-tab');
    const tabContents = section.querySelectorAll('.ap-serial-tab-content');

    tabs.forEach(tab => {
      tab.addEventListener('click', function() {
        const targetId = this.dataset.tab;

        // Update active tab and aria-selected
        tabs.forEach(t => {
          t.classList.remove('active');
          t.setAttribute('aria-selected', 'false');
        });
        this.classList.add('active');
        this.setAttribute('aria-selected', 'true');

        // Update active content
        tabContents.forEach(content => {
          content.classList.remove('active');
          if (content.id === targetId) {
            content.classList.add('active');
          }
        });

        // Track tab switch
        trackEvent('tab_switch', { tab: targetId });
      });
    });

    // Movement number lookup (use class selectors for multi-section support)
    const movementInput = section.querySelector('.ap-movement-input');
    const movementBtn = section.querySelector('.ap-movement-btn');
    const movementResult = section.querySelector('.ap-movement-result');

    if (movementBtn) {
      movementBtn.addEventListener('click', function() {
        const value = movementInput.value.trim();
        if (value) {
          const result = lookupMovementNumber(value);
          displayMovementResult(movementResult, result);
          trackEvent('movement_lookup', { input: value, found: result.found });
        }
      });

      movementInput.addEventListener('keypress', function(e) {
        if (e.key === 'Enter') {
          movementBtn.click();
        }
      });
    }

    // Serial number lookup (use class selectors for multi-section support)
    const serialInput = section.querySelector('.ap-case-input');
    const serialBtn = section.querySelector('.ap-case-btn');
    const serialResult = section.querySelector('.ap-case-result');

    if (serialBtn) {
      serialBtn.addEventListener('click', function() {
        const value = serialInput.value.trim();
        if (value) {
          const result = lookupSerialNumber(value);
          displaySerialResult(serialResult, result);
          trackEvent('serial_lookup', { input: value, found: result.found });
        }
      });

      serialInput.addEventListener('keypress', function(e) {
        if (e.key === 'Enter') {
          serialBtn.click();
        }
      });
    }

    // Info section toggles
    const infoToggles = section.querySelectorAll('.ap-serial-info-toggle');
    infoToggles.forEach(toggle => {
      toggle.addEventListener('click', function() {
        const isOpen = this.classList.toggle('open');
        this.setAttribute('aria-expanded', isOpen);
        const content = this.nextElementSibling;
        content.classList.toggle('open');
      });
    });
  }

  /**
   * Look up a movement number
   */
  function lookupMovementNumber(input) {
    const cleanInput = input.toUpperCase().replace(/\s/g, '');

    // Check for modern alphanumeric format (2017+): AA0001, AB1234, etc.
    if (/^[A-Z]{2}\d{4}$/.test(cleanInput)) {
      return {
        found: true,
        type: 'modern',
        input: cleanInput,
        message: 'This is a modern alphanumeric movement number introduced in 2017.',
        era: '2017 onwards'
      };
    }

    // Check for numeric movement number
    const num = parseInt(cleanInput, 10);
    if (isNaN(num) || num < 2000) {
      return {
        found: false,
        error: 'Invalid movement number. Documented movement numbers start at 2000 (from 1882).'
      };
    }

    if (num > 999999) {
      return {
        found: false,
        error: 'Movement numbers above 999,999 use the alphanumeric format (e.g., AA0001).'
      };
    }

    // Find matching ranges (may have overlaps)
    const matchingRanges = MOVEMENT_RANGES.filter(range =>
      num >= range.min && num <= range.max
    );

    if (matchingRanges.length === 0) {
      // Find closest range
      const closest = findClosestRange(num, MOVEMENT_RANGES);
      return {
        found: false,
        input: num,
        error: `Movement number ${num.toLocaleString()} doesn't match known production ranges.`,
        suggestion: closest ? `Closest era: ${closest.era}` : null
      };
    }

    return {
      found: true,
      type: 'numeric',
      input: num,
      eras: matchingRanges,
      hasOverlap: matchingRanges.length > 1
    };
  }

  /**
   * Look up a serial/case number
   */
  function lookupSerialNumber(input) {
    const cleanInput = input.toUpperCase().replace(/\s+/g, ' ').trim();

    // Check for modern randomized format (2017+)
    // Modern serials are longer alphanumeric strings, often with mixed letters/numbers
    if (isModernRandomizedSerial(cleanInput)) {
      return {
        found: true,
        type: 'modern',
        input: cleanInput,
        message: 'This appears to be a modern randomized serial number introduced in 2017.',
        era: '2017 onwards'
      };
    }

    // Parse prefix and number
    const parsed = parseSerialNumber(cleanInput);

    if (!parsed) {
      return {
        found: false,
        error: 'Invalid serial number format. Expected formats: 12345, B 12345, B12345'
      };
    }

    const { prefix, number } = parsed;
    const range = SERIAL_RANGES[prefix];

    if (!range) {
      return {
        found: false,
        error: `Unknown prefix "${prefix}". Valid prefixes are A through J.`
      };
    }

    if (number < range.min || number > range.max) {
      return {
        found: false,
        prefix,
        number,
        error: `Number ${number.toLocaleString()} is outside the known range for prefix ${prefix} (${range.min.toLocaleString()} - ${range.max.toLocaleString()}).`
      };
    }

    return {
      found: true,
      type: 'prefixed',
      prefix,
      number,
      era: `${range.startYear}-${range.endYear}`,
      startYear: range.startYear,
      endYear: range.endYear,
      range
    };
  }

  /**
   * Parse a serial number into prefix and number
   */
  function parseSerialNumber(input) {
    // Try "B 12345" or "B12345" format
    const prefixMatch = input.match(/^([A-J])\s*(\d+)$/i);
    if (prefixMatch) {
      return {
        prefix: prefixMatch[1].toUpperCase(),
        number: parseInt(prefixMatch[2], 10)
      };
    }

    // Try pure numeric (implies A prefix for early watches)
    const numericMatch = input.match(/^(\d+)$/);
    if (numericMatch) {
      const num = parseInt(numericMatch[1], 10);
      // Numbers 101-105393 are A-prefix (1951-1976)
      if (num >= 101 && num <= 105393) {
        return { prefix: 'A', number: num };
      }
      return null; // Pure numeric outside A range
    }

    return null;
  }

  /**
   * Check if serial appears to be modern randomized format
   */
  function isModernRandomizedSerial(input) {
    // Modern serials are typically 8+ characters with mixed alphanumeric
    // and don't follow the simple "Letter + digits" pattern
    if (input.length < 8) return false;

    // If it matches old format (single letter prefix + digits), it's not modern
    if (/^[A-J]\s*\d+$/i.test(input)) return false;

    // Contains both letters and numbers in mixed positions
    const hasLetters = /[A-Z]/i.test(input);
    const hasNumbers = /\d/.test(input);
    const hasMixedPattern = /[A-Z]\d|\d[A-Z]/i.test(input);

    return hasLetters && hasNumbers && hasMixedPattern;
  }

  /**
   * Find closest range for out-of-range numbers
   */
  function findClosestRange(num, ranges) {
    let closest = null;
    let minDistance = Infinity;

    ranges.forEach(range => {
      const distance = Math.min(
        Math.abs(num - range.min),
        Math.abs(num - range.max)
      );
      if (distance < minDistance) {
        minDistance = distance;
        closest = range;
      }
    });

    return closest;
  }

  /**
   * Display movement number result
   */
  function displayMovementResult(container, result) {
    if (!container) return;

    let html = '';

    if (!result.found) {
      html = `
        <div class="ap-serial-error">
          <strong>Not Found</strong><br>
          ${escapeHtml(result.error)}
          ${result.suggestion ? `<br><em>${escapeHtml(result.suggestion)}</em>` : ''}
        </div>
      `;
    } else if (result.type === 'modern') {
      html = `
        <div class="ap-serial-result-card">
          <div class="ap-serial-result-header">
            <div class="ap-serial-result-icon">AP</div>
            <div class="ap-serial-result-title">
              <h3>Modern Movement Number</h3>
              <p>Alphanumeric format</p>
            </div>
          </div>
          <div class="ap-serial-result-details">
            <div class="ap-serial-detail-item">
              <div class="ap-serial-detail-label">Movement Number</div>
              <div class="ap-serial-detail-value">${escapeHtml(result.input)}</div>
            </div>
            <div class="ap-serial-detail-item">
              <div class="ap-serial-detail-label">Production Era</div>
              <div class="ap-serial-detail-value era">${escapeHtml(result.era)}</div>
            </div>
          </div>
          <div class="ap-serial-modern-notice">
            <h4>Modern Alphanumeric Format</h4>
            <p>${escapeHtml(result.message)} Since 2017, AP transitioned from sequential numbering to an alphanumeric system (AA0001 through AA9999, then AB0001, etc.) to prevent predictable production dating.</p>
          </div>
        </div>
      `;
    } else {
      // Numeric result with potential overlapping eras
      const eras = result.eras;
      const hasOverlap = result.hasOverlap;

      html = `
        <div class="ap-serial-result-card">
          <div class="ap-serial-result-header">
            <div class="ap-serial-result-icon">AP</div>
            <div class="ap-serial-result-title">
              <h3>Movement Number Found</h3>
              <p>Estimated production era</p>
            </div>
          </div>
          <div class="ap-serial-result-details">
            <div class="ap-serial-detail-item">
              <div class="ap-serial-detail-label">Movement Number</div>
              <div class="ap-serial-detail-value">${result.input.toLocaleString()}</div>
            </div>
            <div class="ap-serial-detail-item">
              <div class="ap-serial-detail-label">Production Era</div>
              <div class="ap-serial-detail-value era">${hasOverlap ? 'Multiple possible eras' : escapeHtml(eras[0].era)}</div>
            </div>
          </div>
          ${hasOverlap ? `
            <div class="ap-serial-multiple-eras">
              <h4>This number falls within overlapping production ranges:</h4>
              <ul class="ap-serial-era-list">
                ${eras.map(e => `<li><strong>${escapeHtml(e.era)}</strong> (numbers ${e.min.toLocaleString()} - ${e.max.toLocaleString()})</li>`).join('')}
              </ul>
            </div>
          ` : ''}
          <div class="ap-serial-result-note">
            <strong>Note:</strong> Movement numbers indicate a general era, not an exact production year. Numbers were assigned when movement blanks were acquired, not when watches were completed. Some movements sat for years or even decades before being finished and cased.
          </div>
        </div>
      `;
    }

    // Use DOMPurify if available, otherwise use raw HTML (already escaped)
    if (typeof DOMPurify !== 'undefined') {
      container.innerHTML = DOMPurify.sanitize(html, {
        ALLOWED_TAGS: ['div', 'h3', 'h4', 'p', 'strong', 'em', 'br', 'ul', 'li'],
        ALLOWED_ATTR: ['class']
      });
    } else {
      container.innerHTML = html;
    }
    container.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  /**
   * Display serial number result
   */
  function displaySerialResult(container, result) {
    if (!container) return;

    let html = '';

    if (!result.found) {
      html = `
        <div class="ap-serial-error">
          <strong>Not Found</strong><br>
          ${escapeHtml(result.error)}
        </div>
      `;
    } else if (result.type === 'modern') {
      html = `
        <div class="ap-serial-result-card">
          <div class="ap-serial-result-header">
            <div class="ap-serial-result-icon">AP</div>
            <div class="ap-serial-result-title">
              <h3>Modern Serial Number</h3>
              <p>Randomized alphanumeric format</p>
            </div>
          </div>
          <div class="ap-serial-result-details">
            <div class="ap-serial-detail-item">
              <div class="ap-serial-detail-label">Serial Number</div>
              <div class="ap-serial-detail-value">${escapeHtml(result.input)}</div>
            </div>
            <div class="ap-serial-detail-item">
              <div class="ap-serial-detail-label">Production Era</div>
              <div class="ap-serial-detail-value era">${escapeHtml(result.era)}</div>
            </div>
          </div>
          <div class="ap-serial-modern-notice">
            <h4>Randomized Serial Number</h4>
            <p>${escapeHtml(result.message)} Since 2017, Audemars Piguet adopted randomized alphanumeric serial numbers to reduce counterfeiting and eliminate predictable production dating. These numbers confirm the watch was produced in 2017 or later but cannot indicate a more precise date.</p>
          </div>
        </div>
      `;
    } else {
      // Prefixed result
      html = `
        <div class="ap-serial-result-card">
          <div class="ap-serial-result-header">
            <div class="ap-serial-result-icon">AP</div>
            <div class="ap-serial-result-title">
              <h3>Serial Number Found</h3>
              <p>Estimated production era</p>
            </div>
          </div>
          <div class="ap-serial-result-details">
            <div class="ap-serial-detail-item">
              <div class="ap-serial-detail-label">Serial Number</div>
              <div class="ap-serial-detail-value">${result.prefix === 'A' ? '' : result.prefix + ' '}${result.number.toLocaleString()}</div>
            </div>
            <div class="ap-serial-detail-item">
              <div class="ap-serial-detail-label">Prefix Series</div>
              <div class="ap-serial-detail-value">${result.prefix}-Series</div>
            </div>
            <div class="ap-serial-detail-item">
              <div class="ap-serial-detail-label">Production Era</div>
              <div class="ap-serial-detail-value era">${escapeHtml(result.era)}</div>
            </div>
          </div>
          <div class="ap-serial-result-note">
            <strong>Note:</strong> Serial numbers (case numbers) can estimate a rough production era but cannot date a watch precisely. The ${result.prefix}-series covers numbers ${result.range.min.toLocaleString()} through ${result.range.max.toLocaleString()}, produced approximately ${result.startYear}-${result.endYear}.
          </div>
        </div>
      `;
    }

    // Use DOMPurify if available, otherwise use raw HTML (already escaped)
    if (typeof DOMPurify !== 'undefined') {
      container.innerHTML = DOMPurify.sanitize(html, {
        ALLOWED_TAGS: ['div', 'h3', 'h4', 'p', 'strong', 'em', 'br', 'ul', 'li'],
        ALLOWED_ATTR: ['class']
      });
    } else {
      container.innerHTML = html;
    }
    container.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  /**
   * Escape HTML to prevent XSS
   */
  function escapeHtml(text) {
    if (!text) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Track analytics events
   */
  function trackEvent(action, data) {
    if (typeof gtag === 'function') {
      gtag('event', 'ap_serial_lookup_' + action, data);
    }
  }

  // Initialize on DOM ready
  document.addEventListener('DOMContentLoaded', function() {
    // Find all AP serial lookup sections
    const sections = document.querySelectorAll('.ap-serial-lookup-section');
    sections.forEach(section => {
      const sectionId = section.dataset.sectionId;
      if (sectionId) {
        initSection(sectionId);
      }
    });
  });

  // Expose for external initialization if needed
  window.APSerialLookup = {
    init: initSection,
    lookupMovement: lookupMovementNumber,
    lookupSerial: lookupSerialNumber
  };

})();
